import { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import SelectField from "components/Select";
import { PrimaryButton } from "components/Buttons";
import Colors from "assets/Style/Colors";
import { FontFamily } from "assets";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import InputField from "components/Input";
import SystemServices from "services/System";
import DatePicker from "components/DatePicker";
import VendorServices from "services/Vendor";
import BankServices from "services/Bank";
import FinanceServices from "services/Finance";
import { useAuth } from "context/UseContext";
import { allowedRolesForCash } from "utils";

const BANK_PAGE_LIMIT = 20;

const getSelectedBankCompany = (paymentMethod) => {
    if (paymentMethod?.id === "bank_guc" || paymentMethod?.name === "Bank GUC") {
        return "guc";
    }

    if (
        paymentMethod?.id === "bank_gws" ||
        paymentMethod?.id === "bank" ||
        paymentMethod?.name === "Bank GWS"
    ) {
        return "gws";
    }

    return null;
};

const normalizeBankAccountOptions = (bankRows = [], companyType) =>
    bankRows
        .filter((bank) => bank?.is_active)
        .map((bank) => {
            const account = companyType === "guc" ? bank?.guc_account : bank?.gwc_account;
            const balance = companyType === "guc" ? bank?.gucBal : bank?.gwcBal;

            if (!account?.id) {
                return null;
            }

            return {
                id: account.id,
                name: account.name,
                totalBal: balance ?? 0,
                account_code: account.account_code,
            };
        })
        .filter(Boolean);

const mergeUniqueAccounts = (current, next) => {
    const accountsMap = new Map();
    [...current, ...next].forEach((account) => {
        accountsMap.set(account.id, account);
    });
    return Array.from(accountsMap.values());
};

const fetchMatchingBankAccounts = async ({
    startPage,
    search,
    companyType,
}) => {
    let page = startPage;
    let totalCount = 0;
    let hasMore = false;
    let collectedOptions = [];

    while (true) {
        const params = {
            page,
            limit: BANK_PAGE_LIMIT,
            allow_active_only: 0,
            name: search,
        };

        const { data } = await BankServices.getBanks(params);
        const rows = data?.banks?.rows || [];
        totalCount = Number(data?.banks?.count || 0);
        collectedOptions = mergeUniqueAccounts(
            collectedOptions,
            normalizeBankAccountOptions(rows, companyType)
        );
        hasMore = page * BANK_PAGE_LIMIT < totalCount;

        if (!hasMore || collectedOptions.length >= BANK_PAGE_LIMIT) {
            break;
        }

        page += 1;
    }

    return {
        options: collectedOptions,
        nextPage: page,
        hasMore,
    };
};

function UpdateTT() {

    const navigate = useNavigate();
    const { user } = useAuth();
    const { id } = useParams();

    const { register, handleSubmit, formState: { errors }, setValue } = useForm();
    const [loading, setLoading] = useState(false);

    // *For Handle Date
    const [ttDate, setTtDate] = useState(new Date());

    // *For Vendor Dropdown
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedVendorType, setSelectedVendorType] = useState(null);

    const vendorTypeOptions = [
        { id: "shipping", name: "Shipping" },
        { id: "towing", name: "Towing" },
        { id: "clearance", name: "Clearance" },
        { id: "shipping+towings", name: "Shipping+Towings" },
    ];

    const resolveVendorTypeOption = (vendorType) => {
        if (!vendorType) return null;
        const normalized = vendorType === "towings" ? "towing" : vendorType;
        return vendorTypeOptions.find((opt) => opt.id === normalized) ?? null;
    };

    // *For Currencies
    const [currencies, setCurrencies] = useState([]);
    const [exchangeRate, setExchangeRate] = useState();

    // *For Rates
    const [ttCharges, setTtCharges] = useState(0);
    const [vatCharges, setVatCharges] = useState(0);
    const [fcyAmount, setFcyAmount] = useState(0);
    const [lcyAmount, setLcyAmount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);

    const [ttDetail, setTtDetail] = useState()

    // *For Payment Method
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [damageAccounts, setDamageAccounts] = useState([]);
    const [vendorControlAccounts, setVendorControlAccounts] = useState([]);


    const [selectedVia, setSelectedVia] = useState(null)

    // *For Bank Account
    const [bankAccounts, _setBankAccounts] = useState([]);
    const [_cashierAccounts, setCashierAccounts] = useState([]);
    const [_selectedAccount, setSelectedAccount] = useState(null);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [bankAccountsPage, setBankAccountsPage] = useState(1);
    const [bankAccountsHasMore, setBankAccountsHasMore] = useState(false);
    const [bankAccountsSearch, setBankAccountsSearch] = useState("");
    const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);
    // Tracks whether we've already hydrated the bank/account from ttDetail.
    // Prevents the hydration effect from clobbering the user's later choices
    // when they switch payment method (which resets selectedBankAccount).
    const bankHydratedRef = useRef(false);

    const getTtDetailAccountId = () => {
        const rawId = ttDetail?.account_id ?? ttDetail?.account?.id;
        if (rawId === null || rawId === undefined || rawId === "") return null;
        return String(rawId);
    };

    const normalizePaymentMethodFromDetail = (paymentMedium) => {
        if (!paymentMedium) return null;

        // Backend might return new ids, legacy `bank`, or display labels.
        if (paymentMedium === "bank_guc" || paymentMedium === "Bank GUC") {
            return { id: "bank_guc", name: "Bank GUC" };
        }

        if (paymentMedium === "bank_gws" || paymentMedium === "Bank GWS") {
            return { id: "bank_gws", name: "Bank GWS" };
        }

        if (paymentMedium === "bank") {
            // Legacy default; will be refined to GUC/GWS once we match account_id.
            return { id: "bank_gws", name: "Bank GWS" };
        }

        if (paymentMedium === "cash") {
            return { id: "cash", name: "Cash" };
        }

        // Damages/Vendor Control are stored as their label.
        return { id: paymentMedium, name: paymentMedium };
    };

    // *For Handle Date
    const handleTtDate = (newDate) => {
        try {
            // eslint-disable-next-line eqeqeq
            if (newDate == 'Invalid Date') {
                setTtDate('invalid')
                return
            }
            setTtDate(new Date(newDate))
        } catch (error) {
            ErrorToaster(error)
        }
    }

    // *For Get Currencies
    const getCurrencies = async () => {
        try {
            let params = {
                detailed: true
            }
            const { data } = await SystemServices.getCurrencies(params)
            setCurrencies(data?.currencies)
        } catch (error) {
            ErrorToaster(error)
        }
    }

    // *For Vendor
    const getVendorDropdown = async () => {
        try {
            const { data } = await VendorServices.getVendorDropdown()
            setVendors(data?.vendors)
        } catch (error) {
            ErrorToaster(error)

        }
    }

    const getTTDetail = async () => {
        try {
            let params = {
                tt_id: id
            }
            const { data } = await VendorServices.getTTDetail(params)
            setTtDetail(data?.vendor)
            handleTtDate(data?.vendor?.tt_date)

            console.log(data);


        } catch (error) {
            ErrorToaster(error)
        }
    }

    // *For Get Payment Accounts
    // const getPaymentAccounts = async () => {
    //     try {
    //         let params = {
    //             page: 1,
    //             limit: 1000
    //         }
    //         const { data } = await FinanceServices.getPaymentAccounts(params)
    //         // *Filter only vehicle account
    //         const vehicleAcc = data?.cashierAccounts?.rows?.filter(e => e.unit === 'Shipping')
    //         // *1003 is the cashier role ID if the login user is a cashier then show only their account
    //         if (allowedRolesForCash.includes(user?.role_id)
    //         ) {
    //             const userId = user?.ref_id.split('-')[1]
    //             const filterCashier = vehicleAcc.filter(e => e.user_id == userId)
    //             setCashierAccounts(filterCashier)
    //         } else {
    //             setCashierAccounts(vehicleAcc)
    //         }
    //     } catch (error) {
    //         ErrorToaster(error)
    //     }
    // }

    const getPaymentAccounts = async () => {
        try {
            let params = {
                page: 1,
                limit: 1000,
            };
            const { data } = await FinanceServices.getVendorTTPaymentAccounts(params);

            // Cashier accounts for Shipping unit
            const vehicleAcc = data?.cashierAccounts?.rows?.filter(
                (e) => e.unit === "Shipping"
            );

            if (allowedRolesForCash.includes(user?.role_id)) {
                const userId = user?.ref_id.split("-")[1];
                const filterCashier = vehicleAcc.filter((e) => e.user_id == userId);
                setCashierAccounts(filterCashier);
            } else {
                setCashierAccounts(vehicleAcc);
            }

            // Damage wallet accounts: primary_series = 20091
            const damageAcc = data?.bankAccounts?.rows?.filter(
                (e) => Number(e.primary_series) === 20091
            );
            setDamageAccounts(damageAcc || []);

            // Vendor control accounts: primary_series = 50036
            const vendorControlAcc = data?.bankAccounts?.rows?.filter(
                (e) => Number(e.primary_series) === 50036
            );
            setVendorControlAccounts(vendorControlAcc || []);
        } catch (error) {
            ErrorToaster(error);
        }
    };


    // *For Get Banks
    const getBankAccounts = async ({
        page = 1,
        search = "",
        append = false,
        companyType,
    }) => {
        if (!companyType) {
            setBankAccountOptions([]);
            setBankAccountsPage(1);
            setBankAccountsHasMore(false);
            return;
        }

        setBankAccountsLoading(true);
        try {
            const { options, nextPage, hasMore } = await fetchMatchingBankAccounts({
                startPage: page,
                search,
                companyType,
            });

            setBankAccountOptions((prev) =>
                append ? mergeUniqueAccounts(prev, options) : options
            );
            setBankAccountsPage(nextPage);
            setBankAccountsHasMore(hasMore);
        } catch (error) {
            ErrorToaster(error);
        } finally {
            setBankAccountsLoading(false);
        }
    };

    const loadMoreBankAccounts = () => {
        const companyType = getSelectedBankCompany(selectedPaymentMethod);

        if (bankAccountsLoading || !bankAccountsHasMore || !companyType) {
            return;
        }

        getBankAccounts({
            page: bankAccountsPage + 1,
            search: bankAccountsSearch,
            append: true,
            companyType,
        });
    };

    const handleBankAccountsScroll = (event) => {
        const listboxNode = event.currentTarget;
        const reachedBottom =
            listboxNode.scrollTop + listboxNode.clientHeight >=
            listboxNode.scrollHeight - 8;

        if (reachedBottom) {
            loadMoreBankAccounts();
        }
    };

    // *For Create TT
    const UpdateTT = async (formData) => {
        setLoading(true)
        try {
            let obj = {
                tt_id: id,
                vendor_id: selectedVendor?.id,
                via: selectedVia?.name ?? selectedVia,
                vendor_name: selectedVendor?.name,
                vendor_type: selectedVendorType?.id ?? selectedVendor?.type,
                external_no: formData?.externalRef,
                fcy_amount: parseFloat(fcyAmount || 0).toFixed(2),
                ex_rate: exchangeRate,
                fcy_currency: selectedVendor?.currency,
                lcy_amount: parseFloat(lcyAmount || 0).toFixed(2),
                tt_charges: ttCharges,
                vat_charges: vatCharges,
                total_amount: (parseFloat(totalAmount) + parseFloat(ttCharges) + parseFloat(vatCharges)).toFixed(2),
                notes: formData?.note,
                payment_medium: selectedPaymentMethod?.id,
                tt_date: ttDate,
                account_name: selectedBankAccount?.name,
                account_id:
                    selectedBankAccount?.id
            }
            const { message } = await VendorServices.UpdateTT(obj)
            SuccessToaster(message)
            navigate('/tt-list')
        } catch (error) {
            ErrorToaster(error)
        } finally {
            setLoading(false)
        }
    }

    // *For Handle Select Vendor
    const handleSelectVendor = (data, rateOverride) => {
        try {
            const exRate =
                rateOverride ??
                currencies?.find((e) => e.currency === data?.currency)?.conversion_rate;
            setSelectedVendor(data)
            setValue('vendor', data)

            const resolvedRate = exRate ?? 0;
            setValue('ttRate', resolvedRate)
            setExchangeRate(resolvedRate)

            const autoType = resolveVendorTypeOption(data?.type);
            setSelectedVendorType(autoType);
        } catch (error) {
            ErrorToaster(error)
        }
    }

    // *For Handle Calculate
    const handleCalc = (value) => {
        try {
            const raw = String(value ?? "");
            const normalized = raw.replace(/,/g, ".");
            const fcyValue = parseFloat(normalized) || 0;
            const formattedFcy = Number(fcyValue.toFixed(2));
            const rate = parseFloat(exchangeRate) || 0;
            const aed = formattedFcy * rate;

            setFcyAmount(formattedFcy);
            setLcyAmount(aed);
            setTotalAmount(aed);
        } catch (error) {
            ErrorToaster(error)
        }
    }

    // *For Handle Payment Method
    const handlePaymentMethod = (data) => {
        try {
            setSelectedPaymentMethod(data)
            const companyType = getSelectedBankCompany(data);

            if (companyType) {
                setSelectedAccount(bankAccounts[0])
            } else {
                setSelectedAccount(null)
            }
            // Reset previously selected bank/account when switching method.
            setBankAccountsPage(1);
            setBankAccountsSearch("");
            setBankAccountsHasMore(false);
            setBankAccountOptions([]);
            setSelectedBankAccount(null);
            setValue('bank', null);

            if (companyType) {
                getBankAccounts({
                    page: 1,
                    search: "",
                    append: false,
                    companyType,
                });
            }
        } catch (error) {
            ErrorToaster(error)
        }
    }

    useEffect(() => {
        if (exchangeRate) {
            handleCalc(fcyAmount)
        }
    }, [exchangeRate]);

    useEffect(() => {
        getVendorDropdown()
        getPaymentAccounts()
        getCurrencies()
        getTTDetail()
    }, []);

    useEffect(() => {
        if (ttDetail) {
            setValue('externalRef', ttDetail?.external_no)
            setExchangeRate(ttDetail.ex_rate);

            const baseVendor = ttDetail?.vendor;
            setSelectedVendor(baseVendor)
            handleSelectVendor(baseVendor, ttDetail?.ex_rate)
            console.log(ttDetail?.fcy_amount);
            setValue('vendor', ttDetail?.vendor)
            let parsedFcyAmount = parseFloat(ttDetail?.fcy_amount);
            let parsedTtCharges = parseFloat(ttDetail?.tt_charges);
            let parsedVatCharges = parseFloat(ttDetail?.vat_charges);
            handleCalc(parsedFcyAmount);

            setValue("fcyAmount", parsedFcyAmount);
            setValue("ttCharges", parsedTtCharges);
            setValue("vatCharges", parsedVatCharges);
            setTtCharges(parsedTtCharges);
            setVatCharges(parsedVatCharges);

            const normalizedPaymentMethod = normalizePaymentMethodFromDetail(ttDetail?.payment_medium);
            setSelectedPaymentMethod(normalizedPaymentMethod);
            setValue('paymentMethod', normalizedPaymentMethod);

            // Restore Via selection (SelectField expects an object with `name`).
            if (ttDetail?.via) {
                const viaObj = typeof ttDetail.via === "string" ? { id: ttDetail.via, name: ttDetail.via } : ttDetail.via;
                setSelectedVia(viaObj);
                setValue('via', viaObj);
            }
            setValue('exchange rate', ttDetail.ex_rate)
            setValue('note', ttDetail?.notes || '');
        }

    }, [ttDetail])

    useEffect(() => {
        // Keep RHF state in sync with the Autocomplete selection so validation works.
        if (selectedBankAccount?.id) {
            setValue('bank', selectedBankAccount, { shouldValidate: true, shouldDirty: true });
        } else {
            setValue('bank', null, { shouldValidate: true, shouldDirty: true });
        }
    }, [selectedBankAccount, setValue]);

    useEffect(() => {
        if (!ttDetail) return;
        if (bankHydratedRef.current) return;

        const accountId = getTtDetailAccountId();
        if (!accountId) return;

        const paymentMedium = ttDetail?.payment_medium;

        const hydrateBankPaymentMethod = async () => {
            const fallbackAccount = ttDetail?.account?.id && ttDetail?.account?.name
                ? {
                    id: ttDetail.account.id,
                    name: ttDetail.account.name,
                    totalBal: ttDetail.account.totalBal ?? ttDetail.account.total_balance ?? 0,
                    account_code: ttDetail.account.account_code,
                }
                : null;

            const normalizedPaymentMethod =
                normalizePaymentMethodFromDetail(paymentMedium) ||
                { id: "bank_gws", name: "Bank GWS" };

            const tryHydrateCompany = async (companyType, paymentMethod) => {
                const { data } = await BankServices.getBanks({
                    page: 1,
                    limit: BANK_PAGE_LIMIT,
                    allow_active_only: 0,
                    name: ttDetail?.account?.name || "",
                });

                const options = normalizeBankAccountOptions(
                    data?.banks?.rows || [],
                    companyType
                );
                const matchedAccount = options.find(
                    (account) => String(account?.id) === accountId
                );

                if (!matchedAccount) {
                    return false;
                }

                setSelectedPaymentMethod(paymentMethod);
                setValue('paymentMethod', paymentMethod);
                setBankAccountOptions(options);
                setBankAccountsPage(1);
                setBankAccountsSearch(ttDetail?.account?.name || "");
                setBankAccountsHasMore(
                    BANK_PAGE_LIMIT < Number(data?.banks?.count || 0)
                );
                setSelectedBankAccount(matchedAccount);
                bankHydratedRef.current = true;
                return true;
            };

            try {
                if (
                    paymentMedium === "bank" ||
                    paymentMedium === "bank_guc" ||
                    paymentMedium === "bank_gws" ||
                    paymentMedium === "Bank GUC" ||
                    paymentMedium === "Bank GWS"
                ) {
                    const preferredCompany = getSelectedBankCompany(normalizedPaymentMethod);

                    if (preferredCompany) {
                        const hydratedPreferred = await tryHydrateCompany(
                            preferredCompany,
                            normalizedPaymentMethod
                        );

                        if (hydratedPreferred) {
                            return;
                        }
                    }

                    if (paymentMedium === "bank") {
                        const alternateCompany = preferredCompany === "guc" ? "gws" : "guc";
                        const alternatePaymentMethod =
                            alternateCompany === "guc"
                                ? { id: "bank_guc", name: "Bank GUC" }
                                : { id: "bank_gws", name: "Bank GWS" };

                        const hydratedAlternate = await tryHydrateCompany(
                            alternateCompany,
                            alternatePaymentMethod
                        );

                        if (hydratedAlternate) {
                            return;
                        }
                    }

                    setSelectedPaymentMethod(normalizedPaymentMethod);
                    setValue('paymentMethod', normalizedPaymentMethod);
                    setSelectedBankAccount(fallbackAccount);
                    bankHydratedRef.current = true;
                    return;
                }

                if (paymentMedium === "Damages Claimed - Wallet") {
                    const matchedDamage = damageAccounts.find((a) => String(a?.id) === accountId);
                    if (matchedDamage) {
                        setSelectedBankAccount({
                            id: matchedDamage?.id,
                            name: matchedDamage?.name,
                            totalBal: matchedDamage?.totalBal ?? matchedDamage?.total_balance ?? 0,
                            account_code: matchedDamage?.account_code,
                        });
                        bankHydratedRef.current = true;
                        return;
                    }
                }

                if (paymentMedium === "Vendor Control Account") {
                    const matchedVendor = vendorControlAccounts.find((a) => String(a?.id) === accountId);
                    if (matchedVendor) {
                        setSelectedBankAccount({
                            id: matchedVendor?.id,
                            name: matchedVendor?.name,
                            totalBal: matchedVendor?.totalBal ?? 0,
                            account_code: matchedVendor?.account_code,
                        });
                        bankHydratedRef.current = true;
                        return;
                    }
                }

                if (fallbackAccount) {
                    setSelectedBankAccount(fallbackAccount);
                    bankHydratedRef.current = true;
                }
            } catch (error) {
                ErrorToaster(error);
            }
        };

        hydrateBankPaymentMethod();
    }, [ttDetail, damageAccounts, vendorControlAccounts, setValue]);


    return (
        <Box sx={{ m: 4, p: 5, bgcolor: Colors.white, borderRadius: 3, boxShadow: '0px 8px 18px 0px #9B9B9B1A' }}>

            <Box component="form" onSubmit={handleSubmit(UpdateTT)}>
                <Typography variant="h5" sx={{ color: Colors.charcoalGrey, fontFamily: FontFamily.NunitoRegular, mb: 3 }}>
                    Add TT
                </Typography>
                <Grid container spacing={2} alignItems={'center'}>
                    <Grid item sm={4}>
                        <DatePicker
                            label={'Date'}
                            value={ttDate}
                            onChange={(date) => handleTtDate(date)}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <InputField
                            label={"External Ref No"}
                            placeholder={"External Ref No"}
                            error={errors?.externalRef?.message}
                            register={register("externalRef", {
                                required: "Please enter external ref.",
                            })}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <SelectField
                            label={'Vendor'}
                            options={vendors}
                            selected={selectedVendor}
                            onSelect={(value) => handleSelectVendor(value)}
                            error={errors?.vendor?.message}
                            register={register("vendor", {
                                required: 'Please select vendor.',
                            })}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <InputField
                            label={"Vendor Type"}
                            placeholder={"Auto-selected from vendor"}
                            disabled={true}
                            value={selectedVendorType?.name ?? ""}
                        />
                    </Grid>
                    <Grid item={12} sm={4}>
                        <SelectField
                            label={"Via"}
                            options={[{ id: 'LULU INTL. EXCHANGE', name: 'LULU INTL. EXCHANGE' }, { id: 'AL ANSARI  EXCHANGE', name: 'AL ANSARI  EXCHANGE' }, { id: 'AL FARDAN EXCHANGE', name: 'AL FARDAN EXCHANGE' }, { id: 'AL ROSTAMANI EXCHANGE', name: 'AL ROSTAMANI EXCHANGE' }, { id: 'WALL STREET EXG', name: 'WALL STREET EXG' }]}
                            selected={selectedVia}
                            onSelect={(value) => {
                                setSelectedVia(value);
                            }}
                            error={errors?.via?.message}
                            register={register("via", {
                                required: "Please select via.",
                            })}

                        />
                    </Grid>
                    <Grid item sm={4}>
                        <InputField
                            type={'text'}
                            label={selectedVendor?.currency === 'aed' ? 'FCY Amount (AED)' : 'FCY Amount ($)'}
                            placeholder={selectedVendor?.currency === 'aed' ? 'FCY Amount (AED)' : 'FCY Amount ($)'}
                            inputProps={{ inputMode: 'decimal' }}
                            register={register("fcyAmount", {
                                onChange: (e) => (e.target.value ? handleCalc(e.target.value) : handleCalc(0)),
                                onBlur: (e) => {
                                    const raw = String(e?.target?.value ?? "").trim();
                                    if (!raw) return;
                                    const n = parseFloat(raw.replace(/,/g, "."));
                                    if (Number.isNaN(n)) return;
                                    const fixed = n.toFixed(2);
                                    setValue('fcyAmount', fixed);
                                    handleCalc(fixed);
                                },
                            })}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <InputField
                            disabled={true}
                            label={"LCY Amount (AED)"}
                            placeholder={"LCY Amount (AED)"}
                            value={parseFloat(lcyAmount).toFixed(2)}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <InputField
                            label={'TT Rate (AED)'}
                            placeholder={'TT Rate (AED)'}
                            InputProps={{ inputProps: { min: 0 } }}
                            register={register("ttRate", {
                                onChange: e => e.target.value ? setExchangeRate(e.target.value) : exchangeRate
                            })}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <InputField
                            label={'TT and Bank Charges (AED)'}
                            placeholder={'TT and Bank Charges (AED)'}
                            error={errors?.ttCharges?.message}
                            register={register("ttCharges", {
                                required: "Please enter tt bank charges.",
                                onChange: e => e.target.value ? setTtCharges(e.target.value) : ttCharges
                            })}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <InputField
                            label={'VAT on Bank Charges (AED)'}
                            placeholder={'VAT on Bank Charges '}
                            error={errors?.vatCharges?.message}
                            register={register("vatCharges", {
                                required: "Please enter vat charges.",
                                onChange: e => e.target.value ? setVatCharges(e.target.value) : vatCharges
                            })}
                        />
                    </Grid>
                    <Grid item sm={4}>
                        <SelectField
                            label={'Payment Method'}
                            options={[
                                ...(allowedRolesForCash.includes(user?.role_id)
                                    ? [
                                        {
                                            "id": "cash",
                                            "name": "Cash",
                                        },
                                    ]
                                    : []),
                                {
                                    id: 'bank_gws',
                                    name: 'Bank GWS',
                                },
                                {
                                    id: 'bank_guc',
                                    name: 'Bank GUC',
                                },
                                {
                                    id: "Damages Claimed - Wallet",
                                    name: "Damages Claimed - Wallet",
                                },
                                {
                                    id: "Vendor Control Account",
                                    name: "Vendor Control Account",
                                },
                            ]}
                            selected={selectedPaymentMethod}
                            onSelect={(value) => handlePaymentMethod(value)}
                            error={errors?.paymentMethod?.message}
                            register={register("paymentMethod", {
                                required: 'Please select payment method.',
                            })}
                        />
                    </Grid>
                    {/* <Grid item xs={12} sm={12}> */}
                                {selectedPaymentMethod?.id === "bank_gws" ||
                                  selectedPaymentMethod?.id === "bank_guc" ? (
                                  <>
                                    <Grid item sm={4}>
                                      <SelectField
                                        size="small"
                                        label="Bank Account"
                                                                                options={bankAccountOptions}
                                                                                loading={bankAccountsLoading}
                                        selected={selectedBankAccount}
                                                                                onSearch={(value) => {
                                                                                    const searchValue = value || "";
                                                                                    const companyType = getSelectedBankCompany(selectedPaymentMethod);

                                                                                    setBankAccountsSearch(searchValue);
                                                                                    getBankAccounts({
                                                                                        page: 1,
                                                                                        search: searchValue,
                                                                                        append: false,
                                                                                        companyType,
                                                                                    });
                                                                                }}
                                                                                onListboxScroll={handleBankAccountsScroll}
                                        onSelect={(value) => {
                                          if (!value?.id) {
                                            ErrorToaster(
                                              "DO NOT CLOSE THE TAB REPORT TO IT FOR REVIEW."
                                            );
                                          } else {
                                            setSelectedBankAccount(value);
                                                                                        setValue('bank', value, { shouldValidate: true, shouldDirty: true });
                                          }
                                        }}
                                        error={errors?.bank?.message}
                                        register={register("bank", {
                                                                                    validate: () => {
                                                                                        const needsBank = Boolean(
                                                                                            getSelectedBankCompany(selectedPaymentMethod)
                                                                                        );
                                                                                        if (!needsBank) return true;
                                                                                        return (
                                                                                            !!selectedBankAccount?.id ||
                                                                                            "Please select bank account."
                                                                                        );
                                                                                    },
                                        })}
                                      />
                                    </Grid>
                    
                                    {selectedBankAccount && (
                                      <Grid item xs={12} md={4}>
                                        <InputField
                                          label={"Bank balance "}
                                          disabled={true}
                                          value={parseFloat(selectedBankAccount?.totalBal).toFixed(
                                            2
                                          )}
                                          size="small"
                                        />
                                      </Grid>
                                    )}
                                  </>
                                ) : selectedPaymentMethod?.name === "Damages Claimed - Wallet" ? (
                                  <>
                                    <Grid item xs={12} sm={4}>
                                      <SelectField
                                        size="small"
                                        label="Damage Account"
                                        options={damageAccounts.map((account) => ({
                                          id: account.id,
                                          name: account.name,
                                          totalBal:
                                            account.totalBal ??
                                            account.total_balance ??
                                            0,
                                          account_code: account.account_code,
                                        }))}
                                        selected={selectedBankAccount}
                                        onSelect={(value) => {
                                          if (!value?.id) {
                                            ErrorToaster(
                                              "DO NOT CLOSE THE TAB REPORT TO IT FOR REVIEW."
                                            );
                                          } else {
                                            setSelectedBankAccount(value);
                                                                                        setValue('bank', value, { shouldValidate: true, shouldDirty: true });
                                          }
                                        }}
                                        error={errors?.bank?.message}
                                        register={register("bank", {
                                                                                    validate: () => {
                                                                                        const needsDamage =
                                                                                            selectedPaymentMethod?.name ===
                                                                                            "Damages Claimed - Wallet";
                                                                                        if (!needsDamage) return true;
                                                                                        return (
                                                                                            !!selectedBankAccount?.id ||
                                                                                            "Please select damage account."
                                                                                        );
                                                                                    },
                                        })}
                                      />
                                    </Grid>
                    
                                    {selectedBankAccount && (
                                      <Grid item xs={12} md={4}>
                                        <InputField
                                          label={"Bank balance "}
                                          disabled={true}
                                          value={parseFloat(selectedBankAccount?.totalBal).toFixed(
                                            2
                                          )}
                                          size="small"
                                        />
                                      </Grid>
                                    )}
                                  </>
                                ) : selectedPaymentMethod?.name === "Vendor Control Account" ? (
                                  <>
                                    <Grid item xs={12} sm={4}>
                                      <SelectField
                                        size="small"
                                        label="Vendor Account"
                                        options={vendorControlAccounts.map((account) => ({
                                          id: account.id,
                                          name: account.name,
                                          totalBal: account.totalBal ?? 0,
                                          account_code: account.account_code,
                                        }))}
                                        selected={selectedBankAccount}
                                        onSelect={(value) => {
                                          if (!value?.id) {
                                            ErrorToaster(
                                              "DO NOT CLOSE THE TAB REPORT TO IT FOR REVIEW."
                                            );
                                          } else {
                                            setSelectedBankAccount(value);
                                                                                        setValue('bank', value, { shouldValidate: true, shouldDirty: true });
                                          }
                                        }}
                                        error={errors?.bank?.message}
                                        register={register("bank", {
                                                                                    validate: () => {
                                                                                        const needsVendor =
                                                                                            selectedPaymentMethod?.name ===
                                                                                            "Vendor Control Account";
                                                                                        if (!needsVendor) return true;
                                                                                        return (
                                                                                            !!selectedBankAccount?.id ||
                                                                                            "Please select vendor account."
                                                                                        );
                                                                                    },
                                        })}
                                      />
                                    </Grid>
                    
                                    {selectedBankAccount && (
                                      <Grid item xs={12} md={4}>
                                        <InputField
                                          label={"Bank balance "}
                                          disabled={true}
                                          value={parseFloat(selectedBankAccount?.totalBal).toFixed(
                                            2
                                          )}
                                          size="small"
                                        />
                                      </Grid>
                                    )}
                                  </>
                                ) : (
                                  ""
                                )}
                              {/* </Grid> */}
                    {/* <Grid item sm={4}>
                        {selectedPaymentMethod?.id === 'bank' || selectedPaymentMethod?.name === 'Bank GUC' ? (
                            <>
                                <Grid item xs={12} sm={12}>
                                    <SelectField
                                        size="small"
                                        label="Bank Account"
                                        options={
                                            selectedPaymentMethod?.name === 'Bank GUC'
                                                ? bankAccountsGuc.map((account) => ({
                                                    id: account?.guc_account?.id,
                                                    name: account?.guc_account?.name,
                                                    totalBal: account?.gucBal,
                                                    account_code: account?.guc_account?.account_code,
                                                }))
                                                : bankAccountsGws.map((account) => ({
                                                    id: account?.gwc_account?.id,
                                                    name: account?.gwc_account?.name,
                                                    totalBal: account?.gwcBal,
                                                    account_code: account?.gwc_account?.account_code,
                                                }))
                                        }
                                        selected={selectedBankAccount}
                                        onSelect={(value) => {
                                            if (!value?.id) {
                                                ErrorToaster('DO NOT CLOSE THE TAB REPORT TO IT FOR REVIEW.')
                                            } else {
                                                setSelectedBankAccount(value)
                                            }
                                        }}
                                        error={errors?.bank?.message}
                                        register={register('bank', {
                                            required:
                                                selectedPaymentMethod?.id === 'bank' || selectedPaymentMethod?.name === 'Bank GUC'
                                                    ? 'Please select bank account.'
                                                    : false,
                                        })}
                                    />
                                </Grid>

                                {selectedBankAccount && (
                                    <Grid item xs={12} md={12}>
                                        <InputField
                                            label={'Bank balance '}
                                            disabled={true}
                                            value={parseFloat(
                                                selectedBankAccount?.totalBal
                                            ).toFixed(2)}
                                            size='small'
                                        />
                                    </Grid>
                                )}
                            </>
                        ) : null}
                    </Grid> */}
                    <Grid item sm={4}>
                        <InputField
                            disabled={true}
                            label={'Total Amount'}
                            placeholder={'Total Amount'}
                            InputProps={{ inputProps: { min: 0 } }}
                            value={(parseFloat(totalAmount) + parseFloat(ttCharges || 0) + parseFloat(vatCharges || 0)).toFixed(2)}
                        />
                    </Grid>
                    <Grid item sm={12}>
                        <InputField
                            label={"Accountant Notes"}
                            placeholder={"Accountant Notes"}
                            multiline={true}
                            rows={2}
                            register={register("note")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} >
                        <Box sx={{ textAlign: 'right' }}>
                            <PrimaryButton
                                title="Cancel"
                                onClick={() => navigate('/tt-list')}
                                style={{ marginRight: '1rem' }}
                            />
                            <PrimaryButton
                                title="Update"
                                type="submit"
                                loading={loading}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

        </Box>
    );
}

export default UpdateTT;